import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    Search,
    Filter,
    Download,
    RefreshCw,
    Shield,
    User,
    Clock,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Key,
    LogIn,
    LogOut,
    Edit,
    Trash2,
    Plus,
    Lock,
    Unlock,
    Activity,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Event type icons and colors
const EVENT_CONFIG = {
    LOGIN: { icon: LogIn, color: 'bg-green-100 text-green-700', label: 'Login' },
    LOGOUT: { icon: LogOut, color: 'bg-gray-100 text-gray-700', label: 'Logout' },
    LOGIN_FAILED: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Login Failed' },
    ACCOUNT_LOCKED: { icon: Lock, color: 'bg-red-100 text-red-700', label: 'Account Locked' },
    CREATE: { icon: Plus, color: 'bg-blue-100 text-blue-700', label: 'Created' },
    UPDATE: { icon: Edit, color: 'bg-yellow-100 text-yellow-700', label: 'Updated' },
    DELETE: { icon: Trash2, color: 'bg-red-100 text-red-700', label: 'Deleted' },
    PERMISSION_CHANGE: { icon: Shield, color: 'bg-purple-100 text-purple-700', label: 'Permission Change' },
    ROLE_CHANGE: { icon: User, color: 'bg-purple-100 text-purple-700', label: 'Role Change' },
    PASSWORD_RESET: { icon: Key, color: 'bg-orange-100 text-orange-700', label: 'Password Reset' },
    PASSWORD_CHANGE: { icon: Key, color: 'bg-green-100 text-green-700', label: 'Password Change' },
    '2FA_ENABLED': { icon: Shield, color: 'bg-green-100 text-green-700', label: '2FA Enabled' },
    '2FA_DISABLED': { icon: Unlock, color: 'bg-orange-100 text-orange-700', label: '2FA Disabled' },
    '2FA_FAILED': { icon: AlertTriangle, color: 'bg-red-100 text-red-700', label: '2FA Failed' },
    CLOCK_IN: { icon: Clock, color: 'bg-green-100 text-green-700', label: 'Clock In' },
    CLOCK_OUT: { icon: Clock, color: 'bg-gray-100 text-gray-700', label: 'Clock Out' },
    SESSION_REVOKED: { icon: XCircle, color: 'bg-orange-100 text-orange-700', label: 'Session Revoked' },
    ALL_SESSIONS_REVOKED: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'All Sessions Revoked' },
};

const getEventConfig = (eventType) => {
    return EVENT_CONFIG[eventType] || { icon: Activity, color: 'bg-gray-100 text-gray-700', label: eventType };
};

export default function AuditLogPage() {
    const navigate = useNavigate();
    const { user, isNetworkManager } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [eventTypes, setEventTypes] = useState({});
    
    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const limit = 25;
    
    // Filters
    const [filters, setFilters] = useState({
        eventType: '',
        resourceType: '',
        startDate: '',
        endDate: ''
    });

    useEffect(() => {
        // Check access - only Network Manager/Administrator
        if (!isNetworkManager()) {
            toast.error('Access denied. Network Manager or Administrator only.');
            navigate('/dashboard');
            return;
        }
        
        fetchEventTypes();
        fetchStats();
    }, []);

    useEffect(() => {
        if (isNetworkManager()) {
            fetchLogs();
        }
    }, [page, filters]);

    const fetchEventTypes = async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/audit-logs/event-types`);
            setEventTypes(response.data.event_types || {});
        } catch (error) {
            console.error('Failed to fetch event types:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/audit-logs/stats`);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', limit);
            
            if (filters.eventType) params.append('event_type', filters.eventType);
            if (filters.resourceType) params.append('resource_type', filters.resourceType);
            if (filters.startDate) params.append('start_date', filters.startDate);
            if (filters.endDate) params.append('end_date', filters.endDate);
            
            const response = await axios.get(`${API_URL}/admin/audit-logs?${params.toString()}`);
            setLogs(response.data.logs || []);
            setTotalPages(response.data.pages || 1);
            setTotalLogs(response.data.total || 0);
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
            toast.error('Failed to load audit logs');
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({
            eventType: '',
            resourceType: '',
            startDate: '',
            endDate: ''
        });
        setPage(1);
    };

    const exportLogs = async () => {
        try {
            const params = new URLSearchParams();
            params.append('limit', 10000);
            if (filters.eventType) params.append('event_type', filters.eventType);
            if (filters.startDate) params.append('start_date', filters.startDate);
            if (filters.endDate) params.append('end_date', filters.endDate);
            
            const response = await axios.get(`${API_URL}/admin/audit-logs?${params.toString()}`);
            const exportLogs = response.data.logs || [];
            
            // Convert to CSV
            const headers = ['Timestamp', 'Event Type', 'User', 'Resource', 'Details', 'IP Address'];
            const rows = exportLogs.map(log => [
                log.timestamp,
                log.event_type,
                log.user_name || log.username || 'System',
                `${log.resource_type || ''}/${log.resource_id || ''}`,
                JSON.stringify(log.details || {}),
                log.ip_address || ''
            ]);
            
            const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
            
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            
            toast.success('Audit logs exported');
        } catch (error) {
            toast.error('Failed to export logs');
        }
    };

    return (
        <div className="min-h-screen bg-background" data-testid="audit-log-page">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/dashboard')}
                            className="text-white hover:bg-white/20"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <div>
                            <h1 className="text-lg font-semibold">Audit Logs</h1>
                            <p className="text-xs text-white/70">System activity monitoring</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { fetchLogs(); fetchStats(); }}
                            className="text-white hover:bg-white/20"
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className="w-5 h-5" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={exportLogs}
                            className="hidden sm:flex"
                            data-testid="export-btn"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Export CSV
                        </Button>
                    </div>
                </div>
            </header>

            <div className="p-4 space-y-4 max-w-7xl mx-auto">
                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card>
                            <CardContent className="p-4">
                                <p className="text-sm text-muted-foreground">Total Events</p>
                                <p className="text-2xl font-bold">{stats.total_events?.toLocaleString()}</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                                <p className="text-2xl font-bold">{stats.events_last_24h?.toLocaleString()}</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <p className="text-sm text-muted-foreground">Active Users (24h)</p>
                                <p className="text-2xl font-bold">{stats.unique_users_last_24h}</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <p className="text-sm text-muted-foreground">Top Event</p>
                                <p className="text-lg font-bold truncate">
                                    {stats.events_by_type ? Object.entries(stats.events_by_type).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A' : 'N/A'}
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Filters */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="flex-1 min-w-[150px]">
                                <Label className="text-xs">Event Type</Label>
                                <Select
                                    value={filters.eventType || "all"}
                                    onValueChange={(value) => handleFilterChange('eventType', value === "all" ? "" : value)}
                                >
                                    <SelectTrigger data-testid="filter-event-type">
                                        <SelectValue placeholder="All Events" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Events</SelectItem>
                                        {Object.entries(eventTypes).map(([key, desc]) => (
                                            <SelectItem key={key} value={key}>{key}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="flex-1 min-w-[150px]">
                                <Label className="text-xs">Resource Type</Label>
                                <Select
                                    value={filters.resourceType || "all"}
                                    onValueChange={(value) => handleFilterChange('resourceType', value === "all" ? "" : value)}
                                >
                                    <SelectTrigger data-testid="filter-resource-type">
                                        <SelectValue placeholder="All Resources" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Resources</SelectItem>
                                        <SelectItem value="users">Users</SelectItem>
                                        <SelectItem value="attendance">Attendance</SelectItem>
                                        <SelectItem value="activities">Activities</SelectItem>
                                        <SelectItem value="groups">Groups</SelectItem>
                                        <SelectItem value="permissions">Permissions</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="min-w-[150px]">
                                <Label className="text-xs">Start Date</Label>
                                <Input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    data-testid="filter-start-date"
                                />
                            </div>
                            
                            <div className="min-w-[150px]">
                                <Label className="text-xs">End Date</Label>
                                <Input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    data-testid="filter-end-date"
                                />
                            </div>
                            
                            <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                                Clear
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Logs Table */}
                <Card>
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base">Activity Log</CardTitle>
                            <span className="text-sm text-muted-foreground">
                                Showing {logs.length} of {totalLogs.toLocaleString()} entries
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                                <p className="mt-2 text-muted-foreground">Loading logs...</p>
                            </div>
                        ) : logs.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                <p>No audit logs found</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50">
                                        <tr>
                                            <th className="p-3 text-left font-medium">Timestamp</th>
                                            <th className="p-3 text-left font-medium">Event</th>
                                            <th className="p-3 text-left font-medium">User</th>
                                            <th className="p-3 text-left font-medium">Resource</th>
                                            <th className="p-3 text-left font-medium">Details</th>
                                            <th className="p-3 text-left font-medium">IP Address</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((log) => {
                                            const config = getEventConfig(log.event_type);
                                            const Icon = config.icon;
                                            
                                            return (
                                                <tr 
                                                    key={log.id} 
                                                    className="border-b hover:bg-muted/30 transition-colors"
                                                    data-testid={`log-${log.id}`}
                                                >
                                                    <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">
                                                        {formatTimestamp(log.timestamp)}
                                                    </td>
                                                    <td className="p-3">
                                                        <Badge className={`${config.color} flex items-center gap-1 w-fit`}>
                                                            <Icon className="w-3 h-3" />
                                                            <span className="text-xs">{config.label}</span>
                                                        </Badge>
                                                    </td>
                                                    <td className="p-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                                                <User className="w-3 h-3 text-primary" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-xs">{log.user_name || 'System'}</p>
                                                                <p className="text-xs text-muted-foreground">@{log.username || 'system'}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-3">
                                                        {log.resource_type && (
                                                            <div>
                                                                <p className="text-xs font-medium capitalize">{log.resource_type}</p>
                                                                {log.resource_id && (
                                                                    <p className="text-xs text-muted-foreground font-mono">
                                                                        {log.resource_id.substring(0, 8)}...
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="p-3 max-w-[200px]">
                                                        {log.details && Object.keys(log.details).length > 0 && (
                                                            <p className="text-xs text-muted-foreground truncate">
                                                                {JSON.stringify(log.details).substring(0, 50)}
                                                                {JSON.stringify(log.details).length > 50 && '...'}
                                                            </p>
                                                        )}
                                                    </td>
                                                    <td className="p-3 text-xs text-muted-foreground font-mono">
                                                        {log.ip_address || '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between p-4 border-t">
                                <p className="text-sm text-muted-foreground">
                                    Page {page} of {totalPages}
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        data-testid="prev-page-btn"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        Previous
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        data-testid="next-page-btn"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Security Notice */}
                <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                                <p className="font-medium text-amber-800 dark:text-amber-200">Security Notice</p>
                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                    Audit logs are immutable and cannot be modified or deleted. All user actions are automatically recorded 
                                    for security and compliance purposes. Access to this page is restricted to Network Managers and Administrators.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
